import React, { useMemo, useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, IconButton, TextField, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RouteIcon from '@mui/icons-material/Route';

function asArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') {
    return Object.entries(value).map(([key, item]) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return { id: item.id || item.code || key, ...item };
      }
      return { id: key, title: typeof item === 'string' ? item : key, value: item };
    });
  }
  return [value];
}

function itemTitle(item, fallback = 'Untitled') {
  if (item == null) return fallback;
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return (
    item.title ||
    item.name ||
    item.section_title ||
    item.heading ||
    item.label ||
    item.code ||
    item.id ||
    fallback
  );
}

function itemText(item) {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return (
    item.synopsis ||
    item.description ||
    item.summary ||
    item.purpose ||
    item.content ||
    ''
  );
}

function itemOutcomes(item) {
  if (!item || typeof item !== 'object') return [];
  return asArray(
    item.key_outcomes ||
    item.learning_objectives ||
    item.outcomes ||
    item.objectives
  ).map((o) => (typeof o === 'string' ? o : o.objective || o.description || o.title || o.name || String(o)));
}

function itemTopics(item) {
  if (!item || typeof item !== 'object') return [];
  const nested =
    item.topics ||
    item.skills ||
    item.activities ||
    item.modules ||
    item.content ||
    item.items ||
    [];
  return asArray(nested).map((t, idx) => {
    if (typeof t === 'string' || typeof t === 'number') {
      return { title: String(t), description: '' };
    }
    return {
      title: itemTitle(t, `Item ${idx + 1}`),
      description: itemText(t),
      code: t.code || t.id || null,
      credits: t.credits,
      notional_hours: t.notional_hours,
      weight_pct: t.weight_pct
    };
  });
}

function normalizeChapters(data) {
  if (!data || typeof data !== 'object') return [];

  if (Array.isArray(data.chapters) && data.chapters.length > 0) {
    return data.chapters.map((ch, idx) => ({
      id: ch.id || ch.chapter_id || ch.code || `CH-${idx + 1}`,
      title: itemTitle(ch, `Chapter ${idx + 1}`),
      synopsis: itemText(ch),
      topics: itemTopics(ch),
      outcomes: itemOutcomes(ch),
      duration: ch.duration,
      credits: ch.credits,
      source_codes: asArray(ch.source_codes)
    }));
  }

  if (Array.isArray(data.content_chapters) && data.content_chapters.length > 0) {
    return normalizeChapters({ chapters: data.content_chapters });
  }

  if (Array.isArray(data.contents) && data.contents.length > 0) {
    return data.contents.map((section, idx) => ({
      id: section.id || section.code || `SEC-${idx + 1}`,
      title: itemTitle(section, `Section ${idx + 1}`),
      synopsis: itemText(section),
      topics: itemTopics(section),
      outcomes: itemOutcomes(section),
      duration: section.duration,
      credits: section.credits,
      source_codes: asArray(section.source_codes)
    }));
  }

  const contentStructure = data.content_structure;
  if (contentStructure && typeof contentStructure === 'object') {
    return Object.entries(contentStructure).map(([groupName, groupValue], idx) => {
      const items = asArray(groupValue);
      return {
        id: `GRP-${idx + 1}`,
        title: groupName,
        synopsis: '',
        topics: items.map((item, itemIdx) => ({
          title: itemTitle(item, `Item ${itemIdx + 1}`),
          description: itemText(item),
          code: item.code || item.chapter_id || item.id || null,
          credits: item.credits,
          notional_hours: item.notional_hours,
          weight_pct: item.weight_pct,
          outcomes: itemOutcomes(item),
          source_codes: asArray(item.source_codes)
        })),
        outcomes: [],
        duration: null,
        credits: null,
        source_codes: []
      };
    });
  }

  const nested = data.structure;
  if (nested && typeof nested === 'object') {
    if (Array.isArray(nested)) {
      return normalizeChapters({ chapters: nested });
    }
    return Object.entries(nested).map(([key, section], idx) => {
      const pathways = asArray(section?.pathways || section?.modules || section?.content || section);
      return {
        id: section?.pathway_id || section?.id || key || `SEC-${idx + 1}`,
        title: itemTitle(section, key.replace(/_/g, ' ')),
        synopsis: typeof section?.description === 'string' ? section.description : itemText(section),
        topics: pathways.map((p, pIdx) => {
          if (typeof p === 'string') return { title: p, description: '' };
          const modules = asArray(p.modules || p.content || p.topics);
          return {
            title: itemTitle(p, `Pathway ${pIdx + 1}`),
            description: itemText(p) || (modules.length ? modules.map(itemTitle).join(', ') : ''),
            code: p.pathway_id || p.code || p.id || null
          };
        }),
        outcomes: itemOutcomes(section),
        duration: null,
        credits: null,
        source_codes: []
      };
    });
  }

  return [];
}

function normalizePathways(data) {
  if (!data) return [];
  return asArray(data.learning_pathways).map((p, idx) => {
    if (typeof p === 'string') return { title: p, description: '', content: [] };
    return {
      title: itemTitle(p, `Pathway ${idx + 1}`),
      description: itemText(p),
      content: asArray(p.content || p.modules || p.steps).map(itemTitle)
    };
  });
}

function ReadableValue({ value, depth = 0 }) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-wrap' }}>
        {String(value)}
      </Typography>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <Box component="ul" sx={{ pl: 3, m: 0 }}>
        {value.map((item, idx) => (
          <li key={idx}>
            {typeof item === 'object' ? (
              <ReadableValue value={item} depth={depth + 1} />
            ) : (
              <Typography variant="body2">{String(item)}</Typography>
            )}
          </li>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
    if (entries.length === 0) return null;
    return (
      <Box sx={{ pl: depth > 0 ? 1 : 0 }}>
        {entries.map(([key, val]) => (
          <Box key={key} sx={{ mb: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'capitalize' }}>
              {key.replace(/_/g, ' ')}
            </Typography>
            <ReadableValue value={val} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    );
  }
  return null;
}

export default function StructureDisplay({ data, onEdit, userEdited, isReadOnly = false }) {
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState('');

  const normalized = useMemo(() => {
    if (!data) return null;
    const moduleInfo = data.module || {};
    const chapters = normalizeChapters(data);
    const pathways = normalizePathways(data);
    const title = moduleInfo.name || data.title || null;
    const description = data.description || moduleInfo.purpose || null;
    const code = moduleInfo.code || (data.module_id != null ? String(data.module_id) : null);
    return { chapters, pathways, title, description, code };
  }, [data]);

  if (!data || !normalized) return null;

  const { chapters, pathways, title, description, code } = normalized;
  const canEditCanonicalChapters = Array.isArray(data.chapters) && data.chapters.length > 0;

  const startEdit = (field, value) => {
    setEditingField(field);
    setEditedValue(value);
  };

  const saveEdit = (chapterIdx, topicIdx, field, value) => {
    if (!canEditCanonicalChapters) return;
    const newChapters = [...(data.chapters || [])];

    if (field.startsWith('chapter_title')) {
      newChapters[chapterIdx].title = value;
    } else if (field.startsWith('chapter_synopsis')) {
      newChapters[chapterIdx].synopsis = value;
    } else if (field.startsWith('topic_')) {
      const fieldPart = field.split('_')[1];
      if (!newChapters[chapterIdx].topics) newChapters[chapterIdx].topics = [];
      if (fieldPart === 'title') {
        newChapters[chapterIdx].topics[topicIdx].title = value;
      } else if (fieldPart === 'description') {
        newChapters[chapterIdx].topics[topicIdx].description = value;
      }
    }

    const newData = { ...data, chapters: newChapters };
    if (typeof onEdit === 'function') {
      onEdit(newData);
    }
    setEditingField(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditedValue('');
  };

  return (
    <Box>
      {userEdited && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label="✏️ User Edited"
            size="small"
            color="info"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      {(title || code || description) && (
        <Box sx={{ mb: 3, p: 3, bgcolor: '#f0f9ff', borderRadius: 2, border: '2px solid #3b82f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MenuBookIcon sx={{ color: '#1e40af' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e40af' }}>
              {title || 'Module'}
            </Typography>
          </Box>
          {code && (
            <Chip label={code} size="small" sx={{ mb: 1 }} />
          )}
          {description && (
            <Typography variant="body2" sx={{ color: '#1e3a8a', mt: 1, whiteSpace: 'pre-wrap' }}>
              {description}
            </Typography>
          )}
        </Box>
      )}

      {pathways.length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <RouteIcon sx={{ color: '#0f766e' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f766e' }}>
              Learning Pathways
            </Typography>
          </Box>
          {pathways.map((pathway, idx) => (
            <Box key={idx} sx={{ mb: idx === pathways.length - 1 ? 0 : 1.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {pathway.title}
              </Typography>
              {pathway.description && (
                <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'pre-wrap' }}>
                  {pathway.description}
                </Typography>
              )}
              {pathway.content.length > 0 && (
                <Box component="ul" sx={{ pl: 3, m: 0.5 }}>
                  {pathway.content.map((c, cIdx) => (
                    <li key={cIdx}>
                      <Typography variant="body2">{c}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {chapters.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#475569' }}>
            Content Structure ({chapters.length} {chapters.length === 1 ? 'section' : 'sections'})
          </Typography>
          {chapters.map((chapter, idx) => (
            <Accordion key={`${chapter.id}-${idx}`} defaultExpanded={idx === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                  <Chip label={chapter.id || `CH-${idx + 1}`} size="small" color="primary" />
                  <Typography sx={{ fontWeight: 600 }}>
                    {chapter.title || `Chapter ${idx + 1}`}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {canEditCanonicalChapters ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569' }}>
                          Chapter Title:
                        </Typography>
                        {editingField === `chapter_title_${idx}` ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <TextField
                              value={editedValue}
                              onChange={(e) => setEditedValue(e.target.value)}
                              size="small"
                              fullWidth
                              autoFocus
                              sx={{ flex: 1 }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => saveEdit(idx, null, `chapter_title_${idx}`, editedValue)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEdit}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
                              {chapter.title || `Chapter ${idx + 1}`}
                            </Typography>
                            {!isReadOnly && (
                              <IconButton
                                size="small"
                                onClick={() => startEdit(`chapter_title_${idx}`, chapter.title || `Chapter ${idx + 1}`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', minWidth: 100 }}>
                          Synopsis:
                        </Typography>
                        {editingField === `chapter_synopsis_${idx}` ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                            <TextField
                              value={editedValue}
                              onChange={(e) => setEditedValue(e.target.value)}
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              autoFocus
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={() => saveEdit(idx, null, `chapter_synopsis_${idx}`, editedValue)}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={cancelEdit}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1, color: '#64748b', whiteSpace: 'pre-wrap' }}>
                              {chapter.synopsis || 'No synopsis provided'}
                            </Typography>
                            {!isReadOnly && (
                              <IconButton
                                size="small"
                                onClick={() => startEdit(`chapter_synopsis_${idx}`, chapter.synopsis || '')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </>
                ) : (
                  chapter.synopsis && (
                    <Typography variant="body2" sx={{ mb: 2, color: '#64748b', whiteSpace: 'pre-wrap' }}>
                      {chapter.synopsis}
                    </Typography>
                  )
                )}

                {chapter.topics && chapter.topics.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Topics:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {chapter.topics.map((topic, topicIdx) => (
                        <li key={topicIdx} style={{ marginBottom: 8 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {topic.code ? `${topic.code}: ` : ''}
                            {topic.title}
                          </Typography>
                          {topic.description && (
                            <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'pre-wrap' }}>
                              {topic.description}
                            </Typography>
                          )}
                          {(topic.credits != null || topic.notional_hours != null || topic.weight_pct != null) && (
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                              {topic.credits != null && <Chip label={`${topic.credits} credits`} size="small" variant="outlined" />}
                              {topic.notional_hours != null && <Chip label={`${topic.notional_hours} hours`} size="small" variant="outlined" />}
                              {topic.weight_pct != null && <Chip label={`${topic.weight_pct}%`} size="small" variant="outlined" />}
                            </Box>
                          )}
                          {Array.isArray(topic.outcomes) && topic.outcomes.length > 0 && (
                            <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                              {topic.outcomes.map((obj, objIdx) => (
                                <li key={objIdx}>
                                  <Typography variant="caption" sx={{ color: '#475569' }}>{obj}</Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                          {Array.isArray(topic.source_codes) && topic.source_codes.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {topic.source_codes.map((codeVal, codeIdx) => (
                                <Chip key={codeIdx} label={String(codeVal)} size="small" />
                              ))}
                            </Box>
                          )}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                {chapter.outcomes && chapter.outcomes.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Learning Objectives:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {chapter.outcomes.map((obj, objIdx) => (
                        <li key={objIdx}>
                          <Typography variant="body2">{obj}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}

                {chapter.source_codes && chapter.source_codes.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {chapter.source_codes.map((codeVal, codeIdx) => (
                      <Chip key={codeIdx} label={String(codeVal)} size="small" />
                    ))}
                  </Box>
                )}

                {(chapter.duration || chapter.credits) && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    {chapter.duration && <Chip label={`Duration: ${chapter.duration}`} size="small" variant="outlined" />}
                    {chapter.credits && <Chip label={`${chapter.credits} credits`} size="small" variant="outlined" />}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {chapters.length === 0 && pathways.length === 0 && (
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1.5, fontWeight: 700 }}>
            Structure Details
          </Typography>
          <ReadableValue
            value={Object.fromEntries(
              Object.entries(data).filter(([key]) => !['user_edited', 'generated_at'].includes(key))
            )}
          />
        </Box>
      )}
    </Box>
  );
}
